/**
 * FP3-105A — Disable / Reactivate provider account modal.
 *
 * A reversible SECURITY action (distinct from availability and from
 * permanent delete). Disable: read-only impact preview → strict
 * confirmation (exact provider name + acknowledgment checkbox +
 * optional reason) → immediate access block. Reactivate: single
 * confirm, restores the same account.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";

import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const IMPACT_LABELS = {
  current_students: "Current student relationships",
  historical_students: "Historical student relationships",
  supervised_assistants: "Assistants supervised (org-level)",
  student_level_assistant_relationships: "Student-level assistant assignments",
  future_schedule_blocks: "Future schedule blocks",
  upcoming_sessions: "Upcoming sessions",
  open_notifications: "Unresolved notifications",
  pending_invites: "Pending invites",
  password_reset_tokens: "Active reset tokens",
};

const errDetail = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") return d.message || JSON.stringify(d);
  return e?.message || "Request failed";
};

export default function DisableProviderModal({
  open,
  onOpenChange,
  provider, // { id, name }
  mode, // "disable" | "reactivate"
  asOrg,
  onDone,
}) {
  const isDisable = mode === "disable";
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [ack, setAck] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setConfirmName("");
      setAck(false);
      setReason("");
      setError("");
      return;
    }
    if (isDisable) {
      setLoading(true);
      api
        .providerDisablePreview(provider.id, asOrg ? { as_org: asOrg } : {})
        .then((r) => setPreview(r.data))
        .catch((e) => setError(errDetail(e)))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const nameOk = confirmName.trim() === (provider?.name || "").trim();
  const canDisable = isDisable && !!preview && nameOk && ack && !submitting;

  const submit = async () => {
    setSubmitting(true);
    setError("");
    const params = asOrg ? { as_org: asOrg } : {};
    try {
      if (isDisable) {
        await api.providerDisable(
          provider.id,
          { confirm_name: confirmName.trim(), reason: reason.trim() },
          params
        );
        toast.success(`${provider.name}'s account was disabled.`);
      } else {
        await api.providerReactivate(
          provider.id,
          { confirm_name: provider.name },
          params
        );
        toast.success(`${provider.name}'s account was reactivated.`);
      }
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      setError(errDetail(e));
    } finally {
      setSubmitting(false);
    }
  };

  const impact = preview?.impact || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[88vh] overflow-y-auto"
        data-testid="disable-provider-modal"
      >
        <DialogHeader>
          <DialogTitle className={isDisable ? "text-[#8A5A1E]" : "text-[#1E5A3F]"}>
            {isDisable ? "Disable account" : "Reactivate account"}
          </DialogTitle>
          <DialogDescription>
            {isDisable
              ? "Disabling immediately prevents this user from signing in and revokes their active sessions. Student records and service history remain. Current assignments will be flagged as needing reassignment until you resolve them."
              : `Reactivating restores ${provider?.name}'s account with the same access, supervisors, and school scope. All history is retained.`}
          </DialogDescription>
        </DialogHeader>

        {isDisable ? (
          <>
            {loading && (
              <p className="text-[12px] text-[#6B7280]" data-testid="disable-provider-loading">
                Loading impact…
              </p>
            )}
            {preview && (
              <div className="space-y-3">
                <div
                  className="rounded-md border border-[#EAD8BE] bg-[#FDF8F1] p-3"
                  data-testid="disable-provider-impact"
                >
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-[#8A5A1E] mb-1.5">
                    {provider?.name} · {preview.provider_type}
                    {preview.assistant_type ? ` · ${preview.assistant_type}` : ""}
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                    {Object.entries(IMPACT_LABELS).map(([k, label]) => (
                      <div
                        key={k}
                        className="flex items-center justify-between text-[11.5px]"
                        data-testid={`disable-provider-impact-${k}`}
                      >
                        <span className="text-[#3F4756]">{label}</span>
                        <span className="font-semibold tabular-nums text-[#1F2937]">
                          {Number(impact[k] ?? 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[11px]">
                    Type the exact provider name:{" "}
                    <span className="font-mono font-semibold">{provider?.name}</span>
                  </Label>
                  <Input
                    data-testid="disable-provider-name-input"
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    className="h-8 text-[12px]"
                    placeholder={provider?.name}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px]">Reason (optional)</Label>
                  <Textarea
                    data-testid="disable-provider-reason-input"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="text-[12px] min-h-[54px]"
                    placeholder="e.g. Left the district · security review"
                  />
                </div>
                <label className="flex items-start gap-2 text-[11.5px] text-[#3F4756] cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="disable-provider-ack-checkbox"
                    checked={ack}
                    onChange={(e) => setAck(e.target.checked)}
                    className="mt-0.5 accent-[#8A5A1E]"
                  />
                  <span>
                    I understand this immediately blocks this account from all
                    access.
                  </span>
                </label>
              </div>
            )}
          </>
        ) : (
          <p className="text-[12px] text-[#3F4756]" data-testid="reactivate-provider-body">
            This restores login for <b>{provider?.name}</b>. Relationships that
            were graduated, archived, or removed while disabled are not
            automatically restored.
          </p>
        )}

        {error && (
          <p className="text-[12px] text-[#B85C5C]" data-testid="disable-provider-error">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            data-testid="disable-provider-cancel-btn"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            data-testid={isDisable ? "disable-provider-submit-btn" : "reactivate-provider-submit-btn"}
            size="sm"
            disabled={isDisable ? !canDisable : submitting}
            onClick={submit}
            className={
              isDisable
                ? "bg-[#8A5A1E] hover:bg-[#6F4818] text-white disabled:opacity-40"
                : "bg-[#1E5A3F] hover:bg-[#174733] text-white disabled:opacity-40"
            }
          >
            {submitting
              ? "Working…"
              : isDisable
                ? "Disable account"
                : "Reactivate account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
